import { useState } from "react";
import { useSnackbar } from "notistack5";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
// material
import {
  Link,
  Stack,
  Alert,
  Divider,
  TextField,
  Typography,
  IconButton,
  InputAdornment
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
//
import { login, register, resetPassword } from "../../firebaseConfig";

// ----------------------------------------------------------------------

type Mode = "signIn" | "signUp" | "forgot";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Map Firebase auth error codes to translation keys. Under Firebase "email
// enumeration protection" (default-on for newer projects) both wrong-password
// and unknown-email collapse into `auth/invalid-credential`, so we cannot tell
// the user which it was — the generic `invalidCredential` message covers both
// and nudges toward Google sign-in.
function errorKey(code?: string): string {
  switch (code) {
    case "auth/invalid-email":
      return "auth.errors.invalidEmail";
    case "auth/user-disabled":
      return "auth.errors.userDisabled";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "auth.errors.invalidCredential";
    case "auth/email-already-in-use":
      return "auth.errors.emailInUse";
    case "auth/weak-password":
      return "auth.errors.weakPassword";
    case "auth/too-many-requests":
      return "auth.errors.tooManyRequests";
    case "auth/network-request-failed":
      return "auth.errors.network";
    default:
      return "auth.errors.generic";
  }
}

export default function EmailPasswordAuth() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clear = () => {
    setError(null);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!EMAIL_RE.test(email.trim())) {
      setError(t("auth.errors.invalidEmail"));
      return;
    }
    if (mode !== "forgot" && password.length < 6) {
      setError(t("auth.errors.weakPassword"));
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "signIn") {
        await login(email.trim(), password);
        // onAuthStateChanged takes over: verified → dashboard, unverified → gate.
      } else if (mode === "signUp") {
        await register(email.trim(), password);
        enqueueSnackbar(t("auth.verify.sent"), { variant: "success" });
        // The account is now signed in but unverified — the verify-email gate
        // renders automatically. Nothing else to do here.
      } else {
        await resetPassword(email.trim());
        // Always report success regardless of whether the address exists, so we
        // don't leak which emails are registered (matches Firebase enumeration
        // protection).
        enqueueSnackbar(t("auth.forgot.sent"), { variant: "success" });
        switchMode("signIn");
      }
    } catch (err: any) {
      setError(t(errorKey(err?.code)));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel =
    mode === "signIn"
      ? t("auth.actions.signIn")
      : mode === "signUp"
      ? t("auth.actions.signUp")
      : t("auth.actions.sendReset");

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Divider>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("auth.or")}
        </Typography>
      </Divider>

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth
            type="email"
            autoComplete="username"
            label={t("auth.fields.email")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clear();
            }}
          />

          {mode !== "forgot" && (
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              autoComplete={mode === "signUp" ? "new-password" : "current-password"}
              label={t("auth.fields.password")}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clear();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={submitting}
          >
            {submitLabel}
          </LoadingButton>
        </Stack>
      </form>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        {mode === "signIn" ? (
          <>
            <Link
              component="button"
              type="button"
              variant="subtitle2"
              onClick={() => switchMode("forgot")}
            >
              {t("auth.links.forgot")}
            </Link>
            <Link
              component="button"
              type="button"
              variant="subtitle2"
              onClick={() => switchMode("signUp")}
            >
              {t("auth.links.toSignUp")}
            </Link>
          </>
        ) : (
          <Link
            component="button"
            type="button"
            variant="subtitle2"
            onClick={() => switchMode("signIn")}
          >
            {t("auth.links.toSignIn")}
          </Link>
        )}
      </Stack>
    </Stack>
  );
}
